import { useMemo, useState } from 'react'
import { AlertTriangle, Building2, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useAdminOrganizationMembers,
  useAdminOrganizations,
  useAdminUsers,
  useCreateUserLicense,
  useOrganizationLicenses,
  useUpdateUserLicense,
  type AdminOrganizationOverview,
} from '@/hooks/useAdmin'
import { LicenseManager } from './LicenseManager'

type OrganizationFilter =
  | 'all'
  | 'unlicensed'
  | 'over-quota'
  | 'legacy'

const FILTERS: Array<{ value: OrganizationFilter; label: string }> = [
  { value: 'all', label: 'All organizations' },
  { value: 'unlicensed', label: 'Unlicensed' },
  { value: 'over-quota', label: 'Over quota' },
  { value: 'legacy', label: 'Legacy usage' },
]

function formatQuota(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function formatAttributedQuota(organization: AdminOrganizationOverview): string {
  const finiteQuota = `${formatQuota(organization.finite_attributed_used_km2)}/${formatQuota(organization.annual_quota_km2)} km²`
  const unlimited = organization.license_tiers.includes('LIC-MVPS')

  if (!unlimited) return finiteQuota
  if (organization.annual_quota_km2 > 0) return `Unlimited + ${finiteQuota} finite`
  return 'Unlimited'
}

export function OrganizationAdminView() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<OrganizationFilter>('all')
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  const organizationsQuery = useAdminOrganizations()
  const usersQuery = useAdminUsers()
  const licensesQuery = useOrganizationLicenses(selectedOrgId)
  const membersQuery = useAdminOrganizationMembers(selectedOrgId)
  const updateLicense = useUpdateUserLicense()
  const createLicense = useCreateUserLicense()

  const organizations = organizationsQuery.data ?? []
  const selected =
    organizations.find((org) => org.organization_id === selectedOrgId) ?? null
  const usersById = useMemo(
    () => new Map((usersQuery.data ?? []).map((user) => [user.id, user])),
    [usersQuery.data]
  )

  const visibleOrganizations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    return organizations.filter((org) => {
      const owner = usersById.get(org.owner_id)
      const matchesSearch =
        !normalizedSearch ||
        [
          org.organization_name,
          org.owner_name,
          owner?.email,
          org.organization_id,
        ].some((value) => value?.toLowerCase().includes(normalizedSearch))

      if (!matchesSearch) return false
      if (filter === 'unlicensed') return org.active_license_count === 0
      if (filter === 'over-quota')
        return org.over_quota_license_count > 0
      if (filter === 'legacy') return org.legacy_map_count > 0
      return true
    })
  }, [filter, organizations, search, usersById])

  if (organizationsQuery.isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (organizationsQuery.isError) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
        Failed to load organization licenses. Confirm the organization overview
        migration is applied to this environment.
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/50" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search organizations or owners..."
              className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-3 text-sm text-foreground outline-none focus:border-primary/40"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs transition-colors',
                  filter === option.value
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {visibleOrganizations.length} of {organizations.length} organizations
        </p>

        <div className="overflow-x-auto rounded-lg border border-border bg-background">
          <table className="w-full min-w-[980px] text-left">
            <thead className="border-b border-border bg-secondary/30 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Organization</th>
                <th className="px-4 py-3 font-medium">Members</th>
                <th className="px-4 py-3 font-medium">License</th>
                <th className="px-4 py-3 text-right font-medium">Devices</th>
                <th className="px-4 py-3 text-right font-medium">
                  Attributed quota
                </th>
                <th className="px-4 py-3 text-right font-medium">
                  Legacy usage
                </th>
                <th className="px-4 py-3 font-medium">Term</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleOrganizations.map((org) => (
                <OrganizationRow
                  key={org.organization_id}
                  organization={org}
                  ownerEmail={usersById.get(org.owner_id)?.email}
                  onSelect={() => setSelectedOrgId(org.organization_id)}
                />
              ))}
            </tbody>
          </table>
          {visibleOrganizations.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No organizations match these filters.
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedOrgId(null)}
        >
          <div
            className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto border-l border-border bg-background shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 px-6 py-4 backdrop-blur-sm">
              <div>
                <p className="text-xs text-muted-foreground">
                  Organization license administration
                </p>
                <h2 className="mt-1 text-lg font-medium text-foreground">
                  {selected.organization_name}
                </h2>
              </div>
              <button
                onClick={() => setSelectedOrgId(null)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Close organization details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6 p-6">
              <OrganizationSummary
                organization={selected}
                ownerEmail={usersById.get(selected.owner_id)?.email}
              />

              {(updateLicense.error || createLicense.error) && (
                <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  {(updateLicense.error ?? createLicense.error)?.message}
                </p>
              )}

              <section>
                <h3 className="mb-3 text-sm font-medium text-foreground">
                  Licenses
                </h3>
                {licensesQuery.isError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    <p>Failed to load licenses for this organization.</p>
                    <button
                      onClick={() => void licensesQuery.refetch()}
                      disabled={licensesQuery.isFetching}
                      className="mt-2 text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                    >
                      {licensesQuery.isFetching ? 'Retrying...' : 'Try again'}
                    </button>
                  </div>
                ) : (
                  <LicenseManager
                    licenses={licensesQuery.data ?? []}
                    userId={selected.owner_id}
                    organization={{
                      id: selected.organization_id,
                      name: selected.organization_name,
                      owner_id: selected.owner_id,
                    }}
                    isLoading={licensesQuery.isLoading}
                    onUpdateLicense={(licenseId, updates) =>
                      updateLicense.mutate({ licenseId, updates })
                    }
                    onCreateLicense={(license) => createLicense.mutate(license)}
                    isUpdating={updateLicense.isPending}
                    isCreating={createLicense.isPending}
                  />
                )}
              </section>

              <section>
                <h3 className="mb-3 text-sm font-medium text-foreground">
                  Members
                </h3>
                {membersQuery.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <div className="divide-y divide-border rounded-lg border border-border">
                    {(membersQuery.data ?? []).map((member) => {
                      const user = usersById.get(member.user_id)
                      return (
                        <div
                          key={member.id}
                          className="flex items-center justify-between gap-4 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm text-foreground">
                              {user?.full_name || user?.email || member.user_id}
                            </p>
                            {user?.email && user.full_name && (
                              <p className="truncate text-xs text-muted-foreground">
                                {user.email}
                              </p>
                            )}
                          </div>
                          <span className="text-xs capitalize text-muted-foreground">
                            {member.role}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function OrganizationRow({
  organization,
  ownerEmail,
  onSelect,
}: {
  organization: AdminOrganizationOverview
  ownerEmail?: string
  onSelect: () => void
}) {
  return (
    <tr className="cursor-pointer hover:bg-secondary/20" onClick={onSelect}>
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm text-foreground">
              {organization.organization_name}
            </p>
            <p className="text-xs text-muted-foreground">
              {ownerEmail || organization.owner_name || 'Unknown owner'}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5 text-sm text-muted-foreground">
        {organization.member_count}
      </td>
      <td className="px-4 py-3.5">
        {organization.active_license_count ? (
          <span className="text-sm text-foreground">
            {organization.license_tiers.join(', ')}
          </span>
        ) : (
          <span className="text-sm text-amber-400">Unlicensed</span>
        )}
      </td>
      <td className="px-4 py-3.5 text-right text-sm tabular-nums text-foreground">
        {organization.active_devices}/{organization.total_device_slots}
      </td>
      <td className="px-4 py-3.5 text-right text-sm tabular-nums text-foreground">
        <span>{formatAttributedQuota(organization)}</span>
        {organization.over_quota_license_count > 0 && (
          <span className="mt-1 block text-xs text-destructive">
            {organization.over_quota_license_count}{' '}
            {organization.over_quota_license_count === 1 ? 'license' : 'licenses'} over quota
          </span>
        )}
      </td>
      <td className="px-4 py-3.5 text-right text-sm tabular-nums">
        {organization.legacy_map_count > 0 ? (
          <span className="text-amber-400">
            {formatQuota(organization.legacy_used_km2)} km²
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3.5 text-sm text-muted-foreground">
        {organization.active_license_count > 0 ? 'Perpetual' : '—'}
      </td>
    </tr>
  )
}

function OrganizationSummary({
  organization,
  ownerEmail,
}: {
  organization: AdminOrganizationOverview
  ownerEmail?: string
}) {
  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-4 text-sm">
      <div>
        <p className="text-xs text-muted-foreground">Owner</p>
        <p className="mt-1 text-foreground">
          {ownerEmail || organization.owner_name || organization.owner_id}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Members</p>
        <p className="mt-1 text-foreground">{organization.member_count}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Active devices</p>
        <p className="mt-1 text-foreground">
          {organization.active_devices}/{organization.total_device_slots}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Attributed quota</p>
        <p className="mt-1 text-foreground">
          {formatAttributedQuota(organization)}
        </p>
        {organization.over_quota_license_count > 0 && (
          <p className="mt-1 text-xs text-destructive">
            {organization.over_quota_license_count}{' '}
            {organization.over_quota_license_count === 1 ? 'license is' : 'licenses are'} over quota
          </p>
        )}
      </div>
      {organization.legacy_map_count > 0 && (
        <div className="col-span-2 flex gap-2 rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-xs">
            {formatQuota(organization.legacy_used_km2)} km² across{' '}
            {organization.legacy_map_count} legacy maps is shown separately and
            is not charged to the active entitlement.
          </p>
        </div>
      )}
    </div>
  )
}
